import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { HeroBadge } from '../sections/HeroBadge.jsx';
import { INK, FG_2, FG_3, BLUE, SURFACE, FONTS } from '../sections/theme.js';

const CheckIcon = () => (
    <Box component="svg" viewBox="0 0 24 24" sx={{ width: 18, height: 18, color: BLUE, flex: 'none' }} fill="none">
        <path
            d="M5 12.5l4.5 4.5L19 7"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </Box>
);

const dot = { width: 10, height: 10, borderRadius: '50%', background: '#d8d6de' };
const tag = {
    position: 'absolute',
    top: 14,
    fontFamily: FONTS.mono,
    fontSize: '0.66rem',
    fontWeight: 500,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: '#fff',
    px: 1.4,
    py: 0.75,
    borderRadius: '7px',
    pointerEvents: 'none',
};

export default function WhiteLabel() {
    const { t, i18n } = useTranslation();
    const [pos, setPos] = useState(50);
    const features = t('home.whitelabel.features').split(',');
    const imgLang = i18n.language === 'es' ? 'es' : 'en';

    const onMove = (e) => {
        const r = e.currentTarget.getBoundingClientRect();
        const cx = e.clientX != null ? e.clientX : e.touches && e.touches[0] ? e.touches[0].clientX : 0;
        const p = ((cx - r.left) / r.width) * 100;
        setPos(Math.max(2, Math.min(98, p)));
    };

    return (
        <Box component="section" sx={{ background: SURFACE.beige, py: { xs: 7, md: 10.5 } }}>
            <Container maxWidth="md">
                <Box sx={{ textAlign: 'center', maxWidth: 680, mx: 'auto', mb: 6.5 }}>
                    <HeroBadge label={t('home.whitelabel.eyebrow')} />
                    <Typography
                        component="h2"
                        sx={{
                            fontSize: { xs: '1.875rem', md: '2.25rem' },
                            fontWeight: 700,
                            lineHeight: 1.1,
                            letterSpacing: '-0.02em',
                            color: INK,
                            mb: 1.75,
                        }}
                    >
                        {t('home.whitelabel.title')}
                    </Typography>
                    <Typography sx={{ fontSize: '1.0625rem', fontWeight: 400, color: FG_2, lineHeight: 1.55 }}>
                        {t('home.whitelabel.desc')}
                    </Typography>
                </Box>

                <Box
                    sx={{
                        maxWidth: 720,
                        mx: 'auto',
                        borderRadius: '20px',
                        overflow: 'hidden',
                        boxShadow: '0 32px 74px -30px rgba(20,11,61,.42)',
                        border: '1px solid rgba(26,23,38,.1)',
                        background: '#fff',
                    }}
                >
                    {/* browser chrome */}
                    <Box
                        sx={{
                            height: 38,
                            background: '#f3f1f7',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.9,
                            px: 2,
                            borderBottom: '1px solid #ececf2',
                        }}
                    >
                        <Box sx={dot} />
                        <Box sx={dot} />
                        <Box sx={dot} />
                        <Typography
                            sx={{
                                ml: 1.25,
                                fontFamily: FONTS.mono,
                                fontSize: '0.65rem',
                                fontWeight: 500,
                                color: '#9a97a3',
                            }}
                        >
                            credential viewer
                        </Typography>
                    </Box>

                    {/* compare area */}
                    <Box
                        onPointerMove={onMove}
                        sx={{
                            position: 'relative',
                            width: '100%',
                            aspectRatio: '1440 / 1300',
                            cursor: 'ew-resize',
                            userSelect: 'none',
                            overflow: 'hidden',
                            background: '#fff',
                            touchAction: 'pan-y',
                        }}
                    >
                        <Box
                            component="img"
                            src={`/images/home/wl-branded-${imgLang}.webp`}
                            alt={t('home.whitelabel.label_brand')}
                            draggable={false}
                            loading="lazy"
                            sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                        <Box
                            component="img"
                            src={`/images/home/wl-default-${imgLang}.webp`}
                            alt={t('home.whitelabel.label_default')}
                            draggable={false}
                            loading="lazy"
                            sx={{
                                position: 'absolute',
                                inset: 0,
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                clipPath: `inset(0 ${100 - pos}% 0 0)`,
                            }}
                        />
                        <Typography sx={{ ...tag, left: 14, background: 'rgba(20,11,61,.6)' }}>
                            {t('home.whitelabel.label_default')}
                        </Typography>
                        <Typography sx={{ ...tag, right: 14, background: BLUE }}>
                            {t('home.whitelabel.label_brand')}
                        </Typography>

                        {/* handle */}
                        <Box sx={{ position: 'absolute', top: 0, bottom: 0, left: `${pos}%`, pointerEvents: 'none' }}>
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    bottom: 0,
                                    left: '-1.5px',
                                    width: '3px',
                                    background: '#fff',
                                    boxShadow: '0 0 0 1px rgba(20,11,61,.1)',
                                }}
                            />
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '-23px',
                                    transform: 'translateY(-50%)',
                                    width: 46,
                                    height: 46,
                                    borderRadius: '50%',
                                    background: '#fff',
                                    boxShadow: '0 8px 20px -4px rgba(20,11,61,.45)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'var(--pok-primary, #5124a6)',
                                    fontSize: '1rem',
                                    fontWeight: 700,
                                }}
                            >
                                ‹ ›
                            </Box>
                        </Box>
                    </Box>
                </Box>

                <Typography
                    sx={{
                        textAlign: 'center',
                        fontFamily: FONTS.mono,
                        fontSize: '0.68rem',
                        fontWeight: 500,
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        color: FG_3,
                        mt: 2,
                    }}
                >
                    {t('home.whitelabel.caption')}
                </Typography>

                <Stack
                    direction="row"
                    sx={{
                        flexWrap: 'wrap',
                        justifyContent: 'center',
                        gap: 1.25,
                        mt: 5,
                    }}
                >
                    {features.map((f) => (
                        <Box
                            key={f}
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.9,
                                fontSize: '0.84rem',
                                fontWeight: 500,
                                color: FG_2,
                                background: '#f4f4f7',
                                border: '1px solid rgba(26,23,38,.07)',
                                px: 1.9,
                                py: 1.1,
                                borderRadius: '8px',
                            }}
                        >
                            <CheckIcon />
                            {f.trim()}
                        </Box>
                    ))}
                </Stack>
            </Container>
        </Box>
    );
}
